import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ClockCounterClockwise,
  DownloadSimple,
  Funnel,
  MagnifyingGlass,
  UserCircle,
} from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../app/auth";
import { Drawer, MetricStrip, SearchInput } from "../../components/Primitives";
import { QueryErrorState } from "../../components/States";
import { formatDateTime } from "../../lib/format";
import { useApiQuery } from "../../lib/query";
import { useToast } from "../../components/Toast";
import type { Approval, AuditListResponse, AuditLog } from "../../lib/types";

function saveCsv(contents: string, filename: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

const actionLabels: Record<string, string> = {
  "auth.login": "账号登录",
  "auth.logout": "账号退出",
  "device.list": "查看设备列表",
  "device.detail": "查看设备详情",
  "device.disable": "禁用设备",
  "device.enable": "启用设备",
  "device.unbind": "解绑设备",
  "task.list": "查看任务列表",
  "task.create": "创建任务",
  "task.cancel": "取消任务",
  "task.retry": "重试任务",
  "approval.review": "审批操作",
};

function humanizeAction(action: string) {
  return actionLabels[action] || action.replaceAll(".", " / ");
}

function AuditDetail({ log, onClose }: { log: AuditLog; onClose(): void }) {
  return (
    <Drawer
      title={humanizeAction(log.action)}
      subtitle={`审计记录 #${log.id}`}
      onClose={onClose}
    >
      <div className="audit-event">
        <ClockCounterClockwise size={24} />
        <span><strong>{formatDateTime(log.createdAt)}</strong><small>该时间以服务器记录为准</small></span>
      </div>
      <dl className="detail-list">
        <div><dt>操作人账号</dt><dd>{log.actorId || "系统"}</dd></div>
        <div><dt>资源类型</dt><dd>{log.resourceType || "—"}</dd></div>
        <div><dt>资源编号</dt><dd>{log.resourceId || "—"}</dd></div>
        <div><dt>租户编号</dt><dd>{log.tenantId || "—"}</dd></div>
      </dl>
      <section className="drawer-section">
        <h3>事件详情</h3>
        <pre className="json-preview">{JSON.stringify(log.detail || {}, null, 2)}</pre>
      </section>
    </Drawer>
  );
}

export function AuditPage() {
  const { request, hasPermission } = useAuth();
  const notify = useToast();
  const [searchParams] = useSearchParams();
  const resourceId = searchParams.get("resourceId") || "";
  const [query, setQuery] = useState("");
  const [action, setAction] = useState("");
  const [resourceType, setResourceType] = useState(() => searchParams.get("resourceType") || "");
  const [createdFrom, setCreatedFrom] = useState("");
  const [selected, setSelected] = useState<AuditLog | null>(null);
  const params = new URLSearchParams({ limit: "100" });
  if (action.trim()) params.set("action", action.trim());
  if (resourceType) params.set("resourceType", resourceType);
  if (resourceId) params.set("resourceId", resourceId);
  if (createdFrom) params.set("createdFrom", new Date(`${createdFrom}T00:00:00`).toISOString());
  const path = `/api/audit-logs?${params.toString()}`;
  const auditQuery = useApiQuery<AuditListResponse>(
    ["audit-logs", action, resourceType, resourceId, createdFrom],
    path,
  );
  const logs = auditQuery.data?.items || [];
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return logs.filter((log) =>
      !normalized ||
      [log.id, log.action, log.actorId, log.resourceType, log.resourceId, JSON.stringify(log.detail)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalized),
    );
  }, [logs, query]);
  const actors = new Set(logs.map((log) => log.actorId).filter(Boolean)).size;
  const writes = logs.filter((log) =>
    /(create|update|disable|enable|unbind|cancel|retry|review|delete)/.test(log.action),
  ).length;
  const resourceKinds = new Set(logs.map((log) => log.resourceType).filter(Boolean)).size;
  const exportMutation = useMutation({
    mutationFn: async () => {
      const approval = await request<Approval>("/api/audit-logs/export-approvals", {
        method: "POST",
        body: JSON.stringify({ reason: "管理端审计日志导出" }),
      });
      if (!hasPermission("approvals:review")) {
        return { approval };
      }
      await request(`/api/approvals/${approval.id}/review`, {
        method: "POST",
        body: JSON.stringify({ decision: "approved", comment: "管理员即时审批导出" }),
      });
      const exportParams = new URLSearchParams({ approvalId: approval.id });
      if (action.trim()) exportParams.set("action", action.trim());
      if (resourceType) exportParams.set("resourceType", resourceType);
      if (createdFrom) exportParams.set("createdFrom", new Date(`${createdFrom}T00:00:00`).toISOString());
      const csv = await request<string>(`/api/audit-logs/export.csv?${exportParams}`);
      return { approval, csv };
    },
    onSuccess: ({ approval, csv }) => {
      if (!csv) {
        notify("导出申请已提交", `审批编号 #${approval.id}，审批通过后可下载`);
        return;
      }
      saveCsv(csv, `xmirror-audit-${new Date().toISOString().slice(0, 10)}.csv`);
      notify("审计日志已导出", "文件包含租户、账号和导出时间水印");
    },
  });

  if (auditQuery.isError) {
    return <QueryErrorState onRetry={() => void auditQuery.refetch()} />;
  }

  return (
    <div className="module-page">
      <MetricStrip metrics={[
        { label: "当前结果", value: logs.length, note: "最多读取最近 100 条" },
        { label: "活跃操作人", value: actors, note: "筛选范围内的独立账号", tone: "metric-blue" },
        { label: "写操作", value: writes, note: "状态或数据变更事件", tone: "metric-red" },
        { label: "资源类型", value: resourceKinds, note: "被访问或修改的对象" },
      ]} />
      <section className="table-panel">
        <header className="audit-filter">
          <div className="filter-title"><Funnel size={17} /><span><strong>审计检索</strong><small>{resourceId ? `已定位资源 #${resourceId}` : "支持后端精确筛选与当前页全文检索"}</small></span></div>
          <SearchInput value={query} onChange={setQuery} placeholder="搜索操作、账号或资源" />
          <input value={action} onChange={(event) => setAction(event.target.value)} placeholder="操作编码，如 device.list" />
          <select value={resourceType} onChange={(event) => setResourceType(event.target.value)}>
            <option value="">全部资源</option>
            <option value="device">设备</option>
            <option value="task">任务</option>
            <option value="approval">审批</option>
            <option value="account">账号</option>
          </select>
          <input type="date" value={createdFrom} onChange={(event) => setCreatedFrom(event.target.value)} />
          {hasPermission("audit:export") && (
            <button
              className="secondary-button compact"
              disabled={exportMutation.isPending}
              onClick={() => exportMutation.mutate()}
            >
              <DownloadSimple size={16} />
              {exportMutation.isPending ? "正在申请…" : "审批导出"}
            </button>
          )}
        </header>
        {exportMutation.isError && <div className="inline-error">导出失败，请检查审批权限或稍后重试。</div>}
        <div className="data-table-wrap">
          <table className="data-table audit-table">
            <thead><tr><th>事件</th><th>操作人</th><th>资源</th><th>资源编号</th><th>发生时间</th></tr></thead>
            <tbody>
              {filtered.map((log) => (
                <tr key={log.id} tabIndex={0} onClick={() => setSelected(log)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") setSelected(log); }}>
                  <td><span className="audit-action"><MagnifyingGlass size={18} /><span><strong>{humanizeAction(log.action)}</strong><small>{log.action}</small></span></span></td>
                  <td><span className="actor-cell"><UserCircle size={16} />{log.actorId || "系统"}</span></td>
                  <td>{log.resourceType || "—"}</td>
                  <td>{log.resourceId || "—"}</td>
                  <td>{formatDateTime(log.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!auditQuery.isPending && !filtered.length && <div className="table-empty">没有符合当前条件的审计记录。</div>}
          {auditQuery.isPending && <div className="table-empty">正在读取审计日志…</div>}
        </div>
      </section>
      {selected && <AuditDetail log={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
